import type { ReactNode } from 'react';
import React from 'react';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import {
  faArrowLeft,
  faChartSimple,
  faHome,
  faNewspaper,
  faSearch,
} from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import {
  faChartSimple as faChartSimpleSolid,
  faHome as faHomeSolid,
  faNewspaper as faNewspaperSolid,
  faSearch as faSearchSolid,
} from '@soundxyz/font-awesome/pro-solid-svg-icons/index.mjs';
import { styled } from '@components-shared/src/stitches';
import { resetFilters, resetParams } from '@components/Explore/helpers';
import { setExploreType } from '@components/Explore/useSearchPageStore';
import { ActivityFeedState } from '@components/Feed/store';
import { SoundLogo } from '@components/icons/SoundLogo';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { HeaderLink } from '@components/main-components/nav-bar/HeaderLink';
import { NAV_LINKS } from '@utils/constants';
import { webappUriPath } from '@utils/url';
import { LeftNavType } from './helpers';
import { useLeftNavState } from './store';

const LabelLink = ({
  label,
  icon,
  path,
  isActive,
  onClick,
}: {
  label: string;
  icon?: IconProp;
  path: string;
  isActive: boolean;
  onClick?: () => void;
}) => {
  return (
    <HeaderLink
      label={
        !!icon ? (
          <div className="flex gap-2 pl-1">
            <div className="flex w-8 items-center justify-center">
              <FontAwesomeIcon icon={icon} />
            </div>
            <span>{label}</span>
          </div>
        ) : (
          <span className="ml-1">{label}</span>
        )
      }
      pathnameUri={webappUriPath(path)}
      isActive={isActive}
      onClick={onClick}
    />
  );
};

const IconLink = ({
  icon,
  path,
  isActive,
  onClick,
}: {
  icon: IconProp;
  path: string;
  isActive: boolean;
  onClick?: () => void;
}) => {
  return (
    <HeaderLink
      label={<FontAwesomeIcon icon={icon} />}
      pathnameUri={webappUriPath(path)}
      isActive={isActive}
      onClick={onClick}
    />
  );
};

export const Header = ({
  leftNavType = LeftNavType.DEFAULT,
}: {
  leftNavType?: keyof typeof LeftNavType;
}) => {
  const { pathname } = useRouter();
  const { previousPath } = useLeftNavState();

  const isHomepageActive = pathname === NAV_LINKS.Home;
  const isFeedActive = pathname === NAV_LINKS.Feed;
  const isChartsActive =
    pathname.includes(NAV_LINKS.TopCharts) ||
    pathname.includes(NAV_LINKS.ViralChartsSounds) ||
    pathname.includes(NAV_LINKS.ViralChartsPlaylists);
  const isSearchActive = pathname.includes(NAV_LINKS.Explore);
  const isExploreActive = pathname.includes(NAV_LINKS.Explore);

  let content: ReactNode;
  switch (leftNavType) {
    case LeftNavType.DEFAULT:
      content = (
        <>
          <ExpandedHeader className="expanded-header">
            <LabelLink label="Home" path={NAV_LINKS.Home} isActive={isHomepageActive} />
            <LabelLink
              label="Explore"
              path={NAV_LINKS.Explore}
              isActive={isExploreActive}
              onClick={() => {
                resetParams();
                resetFilters();
                setExploreType({ type: null });
              }}
            />
            <LabelLink
              label="Feed"
              path={NAV_LINKS.Feed}
              isActive={isFeedActive}
              onClick={() => (ActivityFeedState.scrollIndex = 0)}
            />
            <LabelLink
              label="Charts"
              path={NAV_LINKS.ViralChartsSounds}
              isActive={isChartsActive}
            />
          </ExpandedHeader>

          <CollapsedHeader className="collapsed-header">
            <IconLink
              path={NAV_LINKS.Home}
              icon={isHomepageActive ? faHomeSolid : faHome}
              isActive={isHomepageActive}
            />
            <IconLink
              path={NAV_LINKS.Explore}
              icon={isSearchActive ? faSearchSolid : faSearch}
              isActive={isSearchActive}
            />
            <IconLink
              path={NAV_LINKS.Feed}
              icon={isFeedActive ? faNewspaperSolid : faNewspaper}
              isActive={isFeedActive}
              onClick={() => (ActivityFeedState.scrollIndex = 0)}
            />
            <IconLink
              path={NAV_LINKS.ViralChartsSounds}
              icon={isChartsActive ? faChartSimpleSolid : faChartSimple}
              isActive={isChartsActive}
            />
          </CollapsedHeader>
        </>
      );
      break;
    case LeftNavType.DASHBOARD:
      content = (
        <LabelWithArrowLink pathnameUri={webappUriPath(previousPath)} className="label-with-arrow">
          <div>
            <FontAwesomeIcon icon={faArrowLeft} />
          </div>
          <span>Back</span>
        </LabelWithArrowLink>
      );
      break;
    case LeftNavType.UPLOAD:
    case LeftNavType.EDIT_UPLOAD:
      content = (
        <LabelWithArrowLink pathnameUri={NAV_LINKS.Releases} className="label-with-arrow">
          <div>
            <FontAwesomeIcon icon={faArrowLeft} />
          </div>
          <span>Back</span>
        </LabelWithArrowLink>
      );
      break;
    default:
      return null;
  }

  return (
    <div
      className={clsx(
        'flex cursor-pointer flex-col gap-2 px-0 pb-2 pt-4',
        leftNavType === LeftNavType.UPLOAD && 'lt:pb-1',
      )}
    >
      <AccessibleLink pathnameUri="/" className="ml-2 flex h-9 items-center gap-2 lt:ml-5 lt:mt-2">
        <SoundLogo color="black" />
      </AccessibleLink>
      <Links>{content}</Links>
    </div>
  );
};

const linksStyles = {
  flexDirection: 'column',
  gap: 6,
};

const Links = styled('div', {
  ...linksStyles,
  display: 'flex',
  marginTop: 8,
});

const LabelWithArrowLink = styled(AccessibleLink, {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  color: '$base800',
  height: 32,
  borderRadius: 4,
  transition: 'background-color 0.3s ease-in-out',

  '&:hover': {
    backgroundColor: '$base200',
  },

  marginX: 4,
  width: 'calc(100% - 8px)',
  justifyContent: 'center',
  '@laptop': {
    justifyContent: 'unset',
    marginX: 12,
    width: 'calc(100% - 24px)',
    paddingLeft: 8,
  },

  span: {
    title: 'M',
    color: '$base800',
    display: 'none',
    '@laptop': {
      display: 'inline',
    },
  },

  div: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 32,
    width: 32,
    svg: {
      fontSize: 20,
      '@laptop': {
        fontSize: 16,
      },
    },
  },
});

const ExpandedHeader = styled('div', {
  display: 'none',
  ...linksStyles,
  '@laptop': {
    display: 'flex',
  },
});

const CollapsedHeader = styled('div', {
  display: 'flex',
  ...linksStyles,
  '@laptop': {
    display: 'none',
  },
});
